import * as React from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';

import {
  BodyAttachmentSizeClass,
  CommonImageClass,
} from '@/atoms/SourceContent/common';
import { shouldRenderPreview } from '@/utils/shouldRenderPreview';

const ContentBody = styled.p<Pick<Common, 'italic' | 'light' | 'singleLine'>>`
  word-break: break-word;
  ${({ singleLine }) =>
    singleLine ? tw`line-clamp-1 sm:line-clamp-3` : tw`line-clamp-3`}
  ${({ italic }) => italic && tw`italic`}
  ${({ light }) => (light ? tw`text-gray-500` : tw`text-gray-700`)}
  ${tw`flex-1 break-words`}
  mark {
    ${tw`bg-yellow-500 rounded-md px-1 py-0.5`}
  }
`;

const AttachmentContainer = styled.div`
  img {
    ${tw`border border-gray-300 hover:bg-white sm:h-20`}
  }
`;

type BodyImageType = Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'css'>;

export const BodyImage = ({ src, alt, ...rest }: BodyImageType) => {
  const [imageFailed, setImageFailed] = React.useState(false);

  return imageFailed || !shouldRenderPreview(src) ? null : (
    <img // nosemgrep: typescript.react.security.audit.react-http-leak.react-http-leak
      {...rest}
      src={src}
      alt={alt}
      onError={() => setImageFailed(true)}
      className={`${CommonImageClass} ${BodyAttachmentSizeClass} mb-4 sm:mr-4 sm:mb-0 sm:flex-shrink-0 md:w-52`}
    />
  );
};

export const Body: React.FunctionComponent<BodyType> = ({
  text,
  attachment,
  singleLine = false,
  italic = false,
  light = false,
}) => {
  return (
    <div className='w-auto flex flex-col gap-1 body-sm text-gray-700 sm:gap-4 sm:flex-row sm:items-start'>
      {attachment ? (
        <AttachmentContainer className={BodyAttachmentSizeClass}>
          {attachment}
        </AttachmentContainer>
      ) : null}
      <ContentBody singleLine={singleLine} italic={italic} light={light}>
        {text}
      </ContentBody>
    </div>
  );
};

export const CommentBody: React.FunctionComponent<CommentBodyType> = ({
  italic = false,
  leading,
  light = false,
  singleLine = false,
  text,
  trailing,
}) => {
  return (
    <div className='w-auto flex flex-row body-sm text-gray-700 gap-4 sm:items-start md:items-center'>
      {leading ? <>{leading}</> : null}
      <ContentBody singleLine={singleLine} italic={italic} light={light}>
        {text}
      </ContentBody>
      {trailing ? <>{trailing}</> : null}
    </div>
  );
};

type Common = {
  /**
   * italicize content
   */
  italic?: boolean;
  /**
   * less font weight
   */
  light?: boolean;
  /**
   * one line content
   */
  singleLine?: boolean;
  /**
   * message body
   */
  text?: React.ReactNode;
};

export type BodyType = Common & {
  /**
   * attachment
   */
  attachment?: React.ReactNode;
};

export type CommentBodyType = Common & {
  /**
   * leading container
   */
  leading?: React.ReactNode;
  /**
   * trailing container
   */
  trailing?: React.ReactNode;
};
